import { normalizarTexto } from "@/utils/utils";
import { spaceApiEstoqueService } from "../clienteSpace";
import type {
	ParametrosItensClassificacao,
	RespostaArvoreItensClassificacao,
	RespostaConfiguracaoClassificacao,
	RespostaItensClassificacao,
	RespostaTiposClassificacao,
} from "../dtos/classificacao.dto";
import type {
	IListarEstoqueDisponivelPayload,
	IListarEstoqueDisponivelResposta,
	IListarProdutosPrePedidoPayload,
	IListarProdutosPrePedidoResponse,
	IMovimentacaoListarProdutoPayload,
	IMovimentacaoListarProdutoResposta,
	IMovimentacaoProdutoTamanhoResposta,
	IMovimentacaoProdutoTamanhosPayload,
	IMovimentacaoProdutoTipoResposta,
	IMovimentacaoProdutoTiposPayload,
	IReservarEstoqueDisponivelPayload,
	IReservarEstoqueDisponivelResposta,
	IListarTransferenciasProdutoPayload,
	IListarAnotacoesProdutoPayload,
	IInserirAnotacaoProdutoPayload,
	IEditarAnotacaoProdutoPayload,
	IInativarAnotacaoProdutoPayload,
	IListarAnotacoesProdutoResposta,
	IProdutoLupaItem,
	ProdutoImagemRegistro,
	ProdutoImagemUpload,
	ProdutoImagemResposta,
	ProdutoCadastroResposta,
	ProdutoDescricaoConfiguracaoResposta,
	ProdutoParametrosPrecoResposta,
	ProdutoCompletoInput,
	ProdutoCompletoEditInput,
	ResumoProdutoResposta,
	ITamanhoPorProdutoResponse,
	ICestResponse,
	IUpdateValorVendaProdutoRequest,
	IUpdateValorVendaProdutoResponse,
	ITamanhoSugestaoGradeResponse,
	ITamanhoSugestaoGradeConfiguracoesResponse,
	IListarTamanhoSugestaoGradeParams,
	ProdutoListagemAvancadaAgrupadoResposta,
	ProdutoListagemAvancadaResposta,
	ProdutoListagemParametros,
	ProdutoEspecificacaoResposta,
	ProdutoEspecificacaoValorResposta,
	ProdutoCadastroPromocaoResposta,
	AdicionarPromocaoProdutoInput,
	ProdutoEstoqueEmpresasResposta,
} from "../dtos/estoque-service.dto";
import { getCabecalhosGlobaisAutenticacaoSpaceApi } from "../sessaoAutenticacao.space";
import { definirParametroConsulta } from "./utils/parametrosConsulta";
import { listarItensCatalogo } from "./utils/catalogo/catalogoListagem";
import type { CatalogListParams } from "@/configs/configCatalogo";
import { normalizarFiltroListaCsv, normalizarIdsProdutoCsv } from "./utils/estoqueParams";

function validarCsvObrigatorio(valorCsv: string, mensagemErro: string): string {
	if (!valorCsv) throw new Error(mensagemErro);
	return valorCsv;
}

function adicionarPeriodoMovimentacao(
	parametrosConsulta: URLSearchParams,
	dtMovimentoInicio: string | null | undefined,
	dtMovimentoFim: string | null | undefined,
) {
	parametrosConsulta.append("dt_movimento_inicio", normalizarTexto(dtMovimentoInicio));
	parametrosConsulta.append("dt_movimento_fim", normalizarTexto(dtMovimentoFim));
}

// ------------------------------------------------------------------- PRODUTO --------------------------------------
export async function listarProdutosLupa(parametros?: CatalogListParams) {
	const resposta = await listarItensCatalogo<IProdutoLupaItem>("produtos", parametros);

	if (!resposta) throw new Error("Não foi possível listar os produtos.");

	return resposta;
}

export async function listarProdutosPrePedido(req: IListarProdutosPrePedidoPayload, signal?: AbortSignal) {
	const searchParams = new URLSearchParams();

	Object.entries(req).forEach(([chave, valor]) => {
		if (valor === undefined || valor === null || valor === "") return;
		searchParams.set(chave, String(valor));
	});

	const resposta = await spaceApiEstoqueService
		.get("pre_pedido/produtos/listar", { searchParams, signal })
		.json<IListarProdutosPrePedidoResponse>();

	if (!resposta) throw new Error("Erro ao listar produtos do pré-pedido");
	return resposta;
}
const adicionarParametrosLista = (searchParams: URLSearchParams, nome: string, valores?: Array<string | number>) => {
	valores?.forEach((valor) => searchParams.append(nome, String(valor)));
};

const construirParametrosConsultaListagemProdutos = (parametros: ProdutoListagemParametros): URLSearchParams => {
	const searchParams = new URLSearchParams();
	Object.entries(parametros).forEach(([nome, valor]) => {
		if (valor === undefined || Array.isArray(valor)) return;
		searchParams.append(nome, String(valor));
	});
	adicionarParametrosLista(searchParams, "classificacoes", parametros.classificacoes);
	adicionarParametrosLista(searchParams, "classificacoes_buscas", parametros.classificacoes_buscas);
	adicionarParametrosLista(searchParams, "marca_ids", parametros.marca_ids);
	adicionarParametrosLista(searchParams, "fornecedor_ids", parametros.fornecedor_ids);
	adicionarParametrosLista(searchParams, "colecao_ids", parametros.colecao_ids);
	adicionarParametrosLista(searchParams, "cor_ids", parametros.cor_ids);
	adicionarParametrosLista(searchParams, "tamanho_ids", parametros.tamanho_ids);
	adicionarParametrosLista(searchParams, "lojas_com_estoque_ids", parametros.lojas_com_estoque_ids);
	return searchParams;
};

export const listarProdutos = async (
	parametros: ProdutoListagemParametros,
	idUsuario: number,
	signal?: AbortSignal,
): Promise<ProdutoListagemAvancadaResposta> =>
	spaceApiEstoqueService
		.get("produto/listar", {
			searchParams: construirParametrosConsultaListagemProdutos(parametros),
			headers: { "id-usuario": String(idUsuario) },
			signal,
		})
		.json<ProdutoListagemAvancadaResposta>();

export const listarProdutosAgrupados = async (
	parametros: ProdutoListagemParametros,
	idUsuario: number,
	signal?: AbortSignal,
): Promise<ProdutoListagemAvancadaAgrupadoResposta> =>
	spaceApiEstoqueService
		.get("produto/listar-agrupado", {
			searchParams: construirParametrosConsultaListagemProdutos(parametros),
			headers: { "id-usuario": String(idUsuario) },
			signal,
		})
		.json<ProdutoListagemAvancadaAgrupadoResposta>();

export async function listarTamanhoPorProduto(idProduto: number, signal?: AbortSignal) {
	if (!Number.isInteger(idProduto) || idProduto <= 0)
		throw new Error("Código do produto inválido para listar tamanhos.");

	return spaceApiEstoqueService
		.get(`produto/${idProduto}/tamanhos`, {
			signal,
		})
		.json<ITamanhoPorProdutoResponse>();
}

export async function listarSugestoesGradeTamanho(
	parametros: IListarTamanhoSugestaoGradeParams = {},
	signal?: AbortSignal,
) {
	const searchParams = new URLSearchParams();

	Object.entries(parametros).forEach(([chave, valor]) => {
		if (valor === undefined || valor === null || valor === "") return;
		searchParams.set(chave, String(valor));
	});

	return spaceApiEstoqueService
		.get("tamanho/sugestao-grade", {
			searchParams,
			signal,
			headers: getCabecalhosGlobaisAutenticacaoSpaceApi(),
		})
		.json<ITamanhoSugestaoGradeResponse>();
}

export async function listarConfiguracoesSugestaoGradeTamanho(idsProGrade: number[], signal?: AbortSignal) {
	const idsValidos = Array.from(new Set(idsProGrade)).filter((id) => Number.isSafeInteger(id) && id > 0);
	if (idsValidos.length === 0) throw new Error("Informe ao menos uma sugestão de grade válida.");

	return spaceApiEstoqueService
		.get("tamanho/sugestao-grade/configuracoes", {
			signal,
			headers: getCabecalhosGlobaisAutenticacaoSpaceApi(),
			searchParams: { id_pro_grade: idsValidos.join(",") },
		})
		.json<ITamanhoSugestaoGradeConfiguracoesResponse>();
}

export async function getProdutoPreviewImagem(produto_codigo: number, signal?: AbortSignal) {
	const searchParams = new URLSearchParams();
	searchParams.append("produto_codigo", String(produto_codigo));

	const resposta = await spaceApiEstoqueService.get("listar-fotos-produto", {
		searchParams,
		signal,
		throwHttpErrors: false,
	});

	if (!resposta.ok)
		throw new Error(
			`Falha ao obter imagem de produto. Status: ${resposta.status}, Produto Código: ${produto_codigo}`,
		);

	return resposta.json<ProdutoImagemResposta>();
}

export async function listarImagensProduto(idProduto: number, origem: string, signal?: AbortSignal) {
	const imagens: ProdutoImagemRegistro[] = [];
	let offset = 0;
	let continuar = true;
	while (continuar) {
		const resposta = await spaceApiEstoqueService
			.get("produto-imagens/listar", {
				signal,
				timeout: 30000,
				searchParams: {
					id_pro_produto_cab__operador: "eq",
					id_pro_produto_cab__conteudo: idProduto,
					origem__operador: "eq",
					origem__conteudo: origem,
					order_by: "ordem,id",
					offset,
					limit: 100,
				},
			})
			.json<{ items: ProdutoImagemRegistro[]; has_next: boolean }>();
		if (!Array.isArray(resposta.items)) throw new Error("Resposta inválida ao consultar imagens.");
		imagens.push(...resposta.items);
		continuar = resposta.has_next;
		if (continuar && resposta.items.length === 0) throw new Error("Paginação inválida de imagens.");
		offset += resposta.items.length;
	}
	return imagens;
}

export async function excluirImagemProduto(id: number): Promise<void> {
	if (!Number.isSafeInteger(id) || id <= 0) throw new Error("Imagem inválida para exclusão.");
	await spaceApiEstoqueService.delete("produto-imagens/excluir", { json: { ids: [id] }, retry: 0, timeout: 30000 });
}

export function salvarImagemProduto(imagem: ProdutoImagemUpload, signal?: AbortSignal) {
	return spaceApiEstoqueService
		.post("produto-imagens/salvar", {
			json: { imagens: [imagem] },
			signal,
			timeout: 60000,
			retry: 0,
		})
		.json<ProdutoImagemRegistro[]>();
}

export async function getResumoProduto(produto_codigo: number, signal?: AbortSignal) {
	const isIDProdutoValido = Number(produto_codigo) && Number.isInteger(produto_codigo) && produto_codigo > 0;
	if (!isIDProdutoValido) throw new Error("ID de produto inválido");

	const searchParams = new URLSearchParams();
	searchParams.append("produto_codigo", String(produto_codigo));

	const res = await spaceApiEstoqueService.get("produto/listar-resumo-produto", {
		searchParams,
		signal,
		throwHttpErrors: false,
	});

	if (!res.ok)
		throw new Error(`Falha ao obter resumo de produto. Status: ${res.status}, Produto Código: ${produto_codigo}`);

	return res.json<ResumoProdutoResposta>();
}

export const getCadastroProduto = async (produtoId: number, signal?: AbortSignal) => {
	if (!Number.isInteger(produtoId) || produtoId <= 0) throw new Error("ID de produto inválido");

	return spaceApiEstoqueService
		.get(`produto/${produtoId}`, {
			signal,
		})
		.json<ProdutoCadastroResposta>();
};

export const listarConfiguracoesDescricaoProduto = (
	signal?: AbortSignal,
): Promise<ProdutoDescricaoConfiguracaoResposta[]> =>
	spaceApiEstoqueService
		.get("produto/descricao-configuracao", {
			searchParams: {
				aplicar__conteudo: "P",
				aplicar__operador: "eq",
				ativo__conteudo: "true",
				ativo__operador: "eq",
			},
			signal,
		})
		.json<ProdutoDescricaoConfiguracaoResposta[]>();

export const getEstoqueEmpresasProduto = (
	produtoId: number,
	signal?: AbortSignal,
): Promise<ProdutoEstoqueEmpresasResposta> => {
	if (!Number.isInteger(produtoId) || produtoId <= 0) throw new Error("ID de produto inválido");

	return spaceApiEstoqueService
		.get(`produto/${produtoId}/estoque-empresas`, { signal })
		.json<ProdutoEstoqueEmpresasResposta>();
};

export const getParametrosPrecoProduto = (signal?: AbortSignal): Promise<ProdutoParametrosPrecoResposta> =>
	spaceApiEstoqueService
		.get("produto/parametros-preco", {
			signal,
		})
		.json<ProdutoParametrosPrecoResposta>();

export const listarEspecificacoesProduto = (signal?: AbortSignal): Promise<ProdutoEspecificacaoResposta[]> =>
	spaceApiEstoqueService.get("produto/especificacoes", { signal }).json<ProdutoEspecificacaoResposta[]>();

export const listarValoresEspecificacaoProduto = async (
	idProEspecificacaoCab: number,
	signal?: AbortSignal,
): Promise<ProdutoEspecificacaoValorResposta[]> => {
	if (!Number.isInteger(idProEspecificacaoCab) || idProEspecificacaoCab <= 0)
		throw new Error("Especificação inválida para listar valores.");

	return spaceApiEstoqueService
		.get(`produto/especificacoes/${idProEspecificacaoCab}/valores`, { signal })
		.json<ProdutoEspecificacaoValorResposta[]>();
};

export const listarPromocoesProduto = (
	produtoId: number,
	signal?: AbortSignal,
): Promise<ProdutoCadastroPromocaoResposta[]> => {
	if (!Number.isInteger(produtoId) || produtoId <= 0) throw new Error("ID de produto inv�lido");

	return spaceApiEstoqueService
		.get(`produto/${produtoId}/promocoes`, { signal })
		.json<ProdutoCadastroPromocaoResposta[]>();
};

export const adicionarPromocaoProduto = (
	produtoId: number,
	payload: AdicionarPromocaoProdutoInput,
): Promise<ProdutoCadastroPromocaoResposta> => {
	if (!Number.isInteger(produtoId) || produtoId <= 0) throw new Error("ID de produto inv�lido");

	return spaceApiEstoqueService
		.post(`produto/${produtoId}/promocoes`, { json: payload })
		.json<ProdutoCadastroPromocaoResposta>();
};

export const inserirProdutoCompleto = (payload: ProdutoCompletoInput): Promise<ProdutoCadastroResposta> =>
	spaceApiEstoqueService.post("produto/inserir", { json: payload }).json<ProdutoCadastroResposta>();

export const editarProdutoCompleto = (payload: ProdutoCompletoEditInput): Promise<ProdutoCadastroResposta> =>
	spaceApiEstoqueService.put("produto/editar", { json: payload }).json<ProdutoCadastroResposta>();

export const listarCestPorNcm = (
	idFisNcm: string,
	pagina: number,
	limite: number,
	signal?: AbortSignal,
): Promise<ICestResponse> =>
	spaceApiEstoqueService
		.get("fis-cest/listar-por-ncm", {
			signal,
			searchParams: {
				id_fis_ncm: idFisNcm,
				page: String(pagina),
				limit: String(limite),
				order_by: "cest",
			},
		})
		.json<ICestResponse>();

export async function updateValorVendaProduto(req: IUpdateValorVendaProdutoRequest) {
	return spaceApiEstoqueService
		.put("produto-cor/atualizar-venda", {
			json: req,
		})
		.json<IUpdateValorVendaProdutoResponse>();
}

// ------------------------------------------------------------------- CLASSIFICAÇÃO ---------------------------------
export async function listarTiposClassificacao(
	cfg_classificacao_grupo_descricao__conteudo = "Mercadoria para Revenda",
	cfg_classificacao_grupo_descricao__operador = "ilike",
): Promise<RespostaTiposClassificacao> {
	return spaceApiEstoqueService
		.get("classificacao/tipolistar", {
			searchParams: {
				cfg_classificacao_grupo_descricao__conteudo,
				cfg_classificacao_grupo_descricao__operador,
			},
		})
		.json<RespostaTiposClassificacao>();
}

export const listarGruposClassificacao = (): Promise<RespostaTiposClassificacao> =>
	spaceApiEstoqueService
		.get("classificacao/tipolistar", {
			searchParams: { order_by: "cfg_classificacao_grupo_descricao" },
		})
		.json<RespostaTiposClassificacao>();

export async function listarConfiguracaoClassificacao(
	id_cfg_classificacao: number,
): Promise<RespostaConfiguracaoClassificacao> {
	return spaceApiEstoqueService
		.get("classificacao/configuracaolistar", {
			searchParams: {
				order_by: "ordem",
				id_cfg_classificacao: String(id_cfg_classificacao),
			},
		})
		.json<RespostaConfiguracaoClassificacao>();
}

export async function listarItensClassificacao(
	parametros: ParametrosItensClassificacao,
): Promise<RespostaItensClassificacao> {
	const searchParams: Record<string, string> = {
		id_cfg_classificacao: String(parametros.idCfgClassificacao),
	};

	if (parametros.idCfgClassificacaoItens !== undefined) {
		searchParams.id_cfg_classificacao_itens__conteudo = String(parametros.idCfgClassificacaoItens);
		searchParams.id_cfg_classificacao_itens__operador = "eq";
	}

	if (parametros.order_by) searchParams.order_by = parametros.order_by;
	if (parametros.page) searchParams.page = String(parametros.page);
	if (parametros.limit) searchParams.limit = String(parametros.limit);

	if (parametros.descricao__conteudo) {
		searchParams["descricao__conteudo"] = parametros.descricao__conteudo;
		searchParams["descricao__operador"] = parametros.descricao__operador ?? "ilike";
	}

	if (parametros.id__conteudo !== undefined) {
		searchParams["id__conteudo"] = String(parametros.id__conteudo);
		searchParams["id__operador"] = parametros.id__operador ?? "eq";
	}

	return spaceApiEstoqueService
		.get("classificacao/itenslistar", {
			searchParams,
		})
		.json<RespostaItensClassificacao>();
}

export const listarArvoreItensClassificacao = (idCfgClassificacao: number, signal?: AbortSignal) =>
	spaceApiEstoqueService
		.get("classificacao/treeitens", {
			signal,
			searchParams: { id_cfg_classificacao: String(idCfgClassificacao) },
		})
		.json<RespostaArvoreItensClassificacao>();

// ------------------------------------------------------------------- Movimentações de Produto
export async function getMovimentacaoProduto(req: IMovimentacaoListarProdutoPayload) {
	const idsProdutoCsv = validarCsvObrigatorio(
		normalizarIdsProdutoCsv(req.id_pro_produto_cab),
		"Nenhum id_pro_produto_cab válido foi informado para consulta de movimentações",
	);
	const empresasCsv = validarCsvObrigatorio(
		normalizarFiltroListaCsv(req.id_cad_empresa),
		"Nenhum id_cad_empresa válido foi informado para consulta de movimentações",
	);
	const tamanhosCsv = validarCsvObrigatorio(
		normalizarFiltroListaCsv(req.id_pro_tamanho),
		"Nenhum id_pro_tamanho válido foi informado para consulta de movimentações",
	);
	const tiposCsv = normalizarFiltroListaCsv(req.tipo_codigo);

	const searchParams = new URLSearchParams();
	definirParametroConsulta(searchParams, "order_by", req.order_by);
	searchParams.append("id_pro_produto_cab", idsProdutoCsv);
	searchParams.append("id_cad_empresa", empresasCsv);
	adicionarPeriodoMovimentacao(searchParams, req.dt_movimento_inicio, req.dt_movimento_fim);
	definirParametroConsulta(searchParams, "exibe_tamanhos", req.exibe_tamanhos);
	definirParametroConsulta(searchParams, "defeito", req.defeito);
	definirParametroConsulta(searchParams, "entsai", req.entsai);
	definirParametroConsulta(searchParams, "id_pro_tamanho", tamanhosCsv);
	definirParametroConsulta(searchParams, "tipo_codigo", tiposCsv);

	return spaceApiEstoqueService
		.get("movimentacao/listar", {
			searchParams,
		})
		.json<IMovimentacaoListarProdutoResposta[]>();
}

export async function getMovimentacaoProdutoTipos(req: IMovimentacaoProdutoTiposPayload) {
	const idsProdutoCsv = validarCsvObrigatorio(
		normalizarIdsProdutoCsv(req.id_pro_produto_cab),
		"Nenhum id_pro_produto_cab válido foi informado para consulta de tipos de movimentações",
	);
	const empresasCsv = validarCsvObrigatorio(
		normalizarFiltroListaCsv(req.id_cad_empresa),
		"Nenhum id_cad_empresa válido foi informado para consulta de tipos de movimentações",
	);
	const tamanhosCsv = normalizarFiltroListaCsv(req.id_pro_tamanho);
	const tiposCsv = normalizarFiltroListaCsv(req.tipo_codigo);

	const searchParams = new URLSearchParams();
	searchParams.append("id_pro_produto_cab", idsProdutoCsv);
	searchParams.append("id_cad_empresa", empresasCsv);
	adicionarPeriodoMovimentacao(searchParams, req.dt_movimento_inicio, req.dt_movimento_fim);
	definirParametroConsulta(searchParams, "defeito", req.defeito);
	definirParametroConsulta(searchParams, "id_pro_tamanho", tamanhosCsv);
	definirParametroConsulta(searchParams, "tipo_codigo", tiposCsv);

	return spaceApiEstoqueService
		.get("movimentacao/tipos", {
			searchParams,
		})
		.json<IMovimentacaoProdutoTipoResposta[]>();
}

export async function getMovimentacaoProdutoTamanhos(req: IMovimentacaoProdutoTamanhosPayload) {
	const idsProdutoCsv = validarCsvObrigatorio(
		normalizarIdsProdutoCsv(req.id_pro_produto_cab),
		"Nenhum id_pro_produto_cab válido foi informado para consulta de tamanhos de movimentações",
	);
	const empresasCsv = validarCsvObrigatorio(
		normalizarFiltroListaCsv(req.id_cad_empresa),
		"Nenhum id_cad_empresa válido foi informado para consulta de tamanhos de movimentações",
	);

	const searchParams = new URLSearchParams();
	definirParametroConsulta(searchParams, "order_by", req.order_by);
	searchParams.append("id_pro_produto_cab", idsProdutoCsv);
	searchParams.append("id_cad_empresa", empresasCsv);
	definirParametroConsulta(searchParams, "defeito", req.defeito);

	return spaceApiEstoqueService
		.get("movimentacao/tamanhos", {
			searchParams,
		})
		.json<IMovimentacaoProdutoTamanhoResposta[]>();
}

// ---------------------------------------------------- GRADE DISTRIBUIÇÃO DE PRODUTO
export async function getListarEstoqueDisponivel(req: IListarEstoqueDisponivelPayload) {
	const idProdutoCab = Number(req.id_pro_produto_cab);
	if (!Number.isInteger(idProdutoCab) || idProdutoCab <= 0)
		throw new Error("id_pro_produto_cab invalido para listar estoque disponivel");

	const idEmpresa = Number(req.id_cad_empresa);
	if (!Number.isInteger(idEmpresa) || idEmpresa <= 0)
		throw new Error("id_cad_empresa invalido para listar estoque disponivel");

	const params = new URLSearchParams({
		id_pro_produto_cab: String(idProdutoCab),
		id_cad_empresa: String(idEmpresa),
	});

	const res = await spaceApiEstoqueService.get("analiseestoque/transferencia/listar-estoque-disponivel", {
		searchParams: params,
	});

	if (!res.ok) throw new Error(`Falha ao obter estoque disponível. Status: ${res.status}`);

	return res.json<IListarEstoqueDisponivelResposta[]>();
}

export async function reservarEstoqueDisponivel(payload: IReservarEstoqueDisponivelPayload) {
	const res = await spaceApiEstoqueService.post(`analiseestoque/transferencia/reservar-estoque-disponivel`, {
		json: payload,
	});

	const dados = await res.json<IReservarEstoqueDisponivelResposta>();
	if (!dados) throw new Error(`Resposta inesperada ao reservar estoque disponível. Status: ${res.status}`);

	return dados;
}

// ----------------------------------------- TRANSFERÊNCIAS DE PRODUTO ----------------------------------------------
export async function listarTransferenciasProduto(req: IListarTransferenciasProdutoPayload) {
	if (!req.id_pro_produto_cab)
		throw new Error("id_pro_produto_cab é obrigatório para listar transferências de produto");

	const searchParams = new URLSearchParams({
		id_pro_produto_cab: String(req.id_pro_produto_cab).trim(),
	});

	if (req.cad_empresa_destino__conteudo && req.cad_empresa_destino__operador) {
		searchParams.append("cad_empresa_destino__conteudo", String(req.cad_empresa_destino__conteudo).trim());
		searchParams.append("cad_empresa_destino__operador", String(req.cad_empresa_destino__operador).trim());
	}
	if (req.cad_empresa_origem__conteudo && req.cad_empresa_origem__operador) {
		searchParams.append("cad_empresa_origem__conteudo", String(req.cad_empresa_origem__conteudo).trim());
		searchParams.append("cad_empresa_origem__operador", String(req.cad_empresa_origem__operador).trim());
	}
	if (Array.isArray(req.dt_emissao) && req.dt_emissao__operador) {
		const periodoEmissaoNormalizado = req.dt_emissao
			.map((data) => String(data ?? "").trim())
			.filter(Boolean)
			.slice(0, 2);

		if (periodoEmissaoNormalizado.length === 2) {
			searchParams.append("dt_emissao__operador", String(req.dt_emissao__operador).trim());
			periodoEmissaoNormalizado.forEach((data) => searchParams.append("dt_emissao", data));
		}
	}

	const res = await spaceApiEstoqueService.get(
		`analiseestoque/transferencia/listar-transferencias?${searchParams.toString()}`,
	);

	if (!res.ok) throw new Error(`Falha ao listar transferências do produto. Status: ${res.status}`);

	return await res.json();
}

// --------------------------------------------- ANOTAÇÃO DE PRODUTO ---------------------------------------------
export async function listarAnotacoesProduto(req: IListarAnotacoesProdutoPayload) {
	if (!req) throw new Error("Requisição é obrigatória para listar anotações de produto");

	const searchParams = new URLSearchParams();

	definirParametroConsulta(searchParams, "order_by", req.order_by);
	definirParametroConsulta(searchParams, "id__conteudo", req.id__conteudo);
	definirParametroConsulta(searchParams, "id__operador", req.id__operador);
	definirParametroConsulta(searchParams, "anotacao__conteudo", req.anotacao__conteudo);
	definirParametroConsulta(searchParams, "anotacao__operador", req.anotacao__operador);
	definirParametroConsulta(searchParams, "tela_origem__conteudo", req.tela_origem__conteudo);
	definirParametroConsulta(searchParams, "tela_origem__operador", req.tela_origem__operador);
	definirParametroConsulta(searchParams, "ativo__conteudo", req.ativo__conteudo);
	definirParametroConsulta(searchParams, "ativo__operador", req.ativo__operador);
	definirParametroConsulta(
		searchParams,
		"cad_pessoa_funcionario__id__conteudo",
		req.cad_pessoa_funcionario__id__conteudo,
	);
	definirParametroConsulta(
		searchParams,
		"cad_pessoa_funcionario__id__operador",
		req.cad_pessoa_funcionario__id__operador,
	);
	definirParametroConsulta(searchParams, "dt_atualizacao__conteudo", req.dt_atualizacao__conteudo);
	definirParametroConsulta(searchParams, "dt_atualizacao__operador", req.dt_atualizacao__operador);
	definirParametroConsulta(searchParams, "id_pro_produto_cab__conteudo", req.id_pro_produto_cab__conteudo);
	definirParametroConsulta(searchParams, "id_pro_produto_cab__operador", req.id_pro_produto_cab__operador);
	definirParametroConsulta(searchParams, "limit", req.limit);
	definirParametroConsulta(searchParams, "page", req.page);
	definirParametroConsulta(searchParams, "offset", req.offset);

	const res = await spaceApiEstoqueService.get("produto-anotacao/listar", {
		searchParams,
	});

	if (!res.ok) throw new Error(`Falha ao listar anotações do produto. Status: ${res.status}`);

	return await res.json<IListarAnotacoesProdutoResposta>();
}

export async function inserirAnotacaoProduto(req: IInserirAnotacaoProdutoPayload) {
	if (!req) throw new Error("Requisição é obrigatória para inserir anotação de produto");

	const res = await spaceApiEstoqueService.post(`produto-anotacao/inserir`, {
		headers: { "Content-Type": "application/json" },
		json: req,
	});

	if (!res.ok) throw new Error(`Falha ao inserir anotação do produto. Status: ${res.status}`);

	return await res.json<IListarAnotacoesProdutoResposta["items"]>();
}

export async function editarAnotacaoProduto(req: IEditarAnotacaoProdutoPayload) {
	if (!req) throw new Error("Requisição é obrigatória para editar anotação de produto");

	const res = await spaceApiEstoqueService.put(`produto-anotacao/editar`, {
		headers: { "Content-Type": "application/json" },
		json: req,
	});

	if (!res.ok) throw new Error(`Falha ao editar anotação do produto. Status: ${res.status}`);

	return await res.json<IListarAnotacoesProdutoResposta["items"]>();
}

export async function inativarAnotacaoProduto(req: IInativarAnotacaoProdutoPayload) {
	if (!req) throw new Error("Requisição é obrigatória para inativar anotação de produto");

	const res = await spaceApiEstoqueService.put(`produto-anotacao/inativar`, {
		headers: { "Content-Type": "application/json" },
		json: req,
	});

	if (!res.ok) throw new Error(`Falha ao inativar anotação do produto. Status: ${res.status}`);

	return await res.json<IListarAnotacoesProdutoResposta["items"]>();
}
